"use client";

import React, { useState } from "react";
import { ArrowLeft, ArrowRight, Cpu, Clock, Users, GraduationCap, Cog, Code as Code2, CircleCheck as CheckCircle2, Languages, ChevronRight, Lightbulb, Wrench, Monitor, Presentation, FerrisWheel, Compass, Sparkles, Laptop, Layers, Check } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Toggle } from "@/components/ui/toggle";

// Semantic Breadcrumb Components
function Breadcrumb({
  className,
  ...props
}: React.ComponentProps<"nav">) {
  return <nav aria-label="Breadcrumb" data-slot="breadcrumb" className={className} {...props} />;
}
function BreadcrumbList({
  className,
  ...props
}: React.ComponentProps<"ol">) {
  return <ol data-slot="breadcrumb-list" className={`flex flex-wrap items-center gap-1.5 text-xs sm:text-sm font-medium text-muted-foreground ${className ?? ""}`} {...props} />;
}
function BreadcrumbItem({
  className,
  ...props
}: React.ComponentProps<"li">) {
  return <li data-slot="breadcrumb-item" className={`inline-flex items-center gap-1.5 ${className ?? ""}`} {...props} />;
}
function BreadcrumbLink({
  asChild,
  className,
  ...props
}: React.ComponentProps<typeof Link> & {
  asChild?: boolean;
}) {
  return <Link data-slot="breadcrumb-link" className={`transition-colors hover:text-primary ${className ?? ""}`} {...props} />;
}
function BreadcrumbPage({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return <span data-slot="breadcrumb-page" role="link" aria-disabled="true" aria-current="page" className={`font-semibold text-foreground ${className ?? ""}`} {...props} />;
}
function BreadcrumbSeparator({
  children,
  className,
  ...props
}: React.ComponentProps<"li">) {
  return <li data-slot="breadcrumb-separator" role="presentation" aria-hidden="true" className={`[&>svg]:size-3.5 text-muted-foreground shrink-0 ${className ?? ""}`} {...props}>
      {children ?? <ChevronRight className="size-3.5" />}
    </li>;
}
export default function CourseDetailHP1() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  const content = {
    vi: {
      breadcrumbs: {
        home: "Trang chủ",
        pathway: "Lộ trình Robotics",
        track: "Robotics 7+ tuổi",
        current: "HP1 – Kỹ sư cơ khí nhí"
      },
      code: "HP1",
      name: "Kỹ sư cơ khí nhí",
      subtitle: "Khóa học chế tạo và lập trình robotics nền tảng",
      conceptQuote: "Bắt đầu lập trình robot thực sự.",
      primaryCta: "ĐĂNG KÝ TƯ VẤN",
      secondaryCta: "XEM TOÀN BỘ LỘ TRÌNH",
      ageLabel: "Độ tuổi",
      ageValue: "Từ 7 tuổi",
      platformLabel: "Bộ giáo cụ",
      platformValue: "WhalesBot AI Module 1S",
      durationLabel: "Thời lượng",
      durationValue: "12 buổi × 90 phút",
      groupSizeLabel: "Quy mô lớp",
      groupSizeValue: "2–3 học sinh/bộ kit",
      prerequisiteLabel: "Điều kiện tiên quyết",
      prerequisiteValue: "Hoàn thành D4 hoặc theo điều kiện đầu vào",
      // Target Audience
      targetAudienceTitle: "Khóa học này dành cho ai?",
      targetAudienceBody: "Dành cho học viên từ 7 tuổi, sau D4 hoặc theo điều kiện đầu vào được quy định trong chương trình. HP1 là bước chuyển sang lập trình robot thực tế với WhalesBot AI Module 1S.",
      // 4-Step Methodology
      methodologyTitle: "Phương pháp học tập 4 bước",
      methodologySubtitle: "Quy trình chuẩn giúp học viên tiếp cận kỹ thuật và tư duy lập trình bài bản",
      methodologySteps: [{
        num: "01",
        title: "Khởi động",
        desc: "Tìm hiểu thử thách thực tế, quan sát nguyên lý cơ học và xác định mục tiêu của buổi học."
      }, {
        num: "02",
        title: "Lắp ráp / Thực hành",
        desc: "Hiện thực hóa kết cấu cơ học với controller, động cơ, bánh răng, đòn bẩy và cơ cấu tay quay."
      }, {
        num: "03",
        title: "Lập trình / Tư duy",
        desc: "Sử dụng phần mềm Block Studio để xây dựng thuật toán tuần tự, kiểm soát góc quay và vòng lặp."
      }, {
        num: "04",
        title: "Trình bày",
        desc: "Kiểm tra vận hành robot, đo đạc thông số thực tế và chia sẻ giải pháp kỹ thuật trước lớp."
      }],
      // 12 Sessions in 6 Stages
      timelineTitle: "Lộ trình 12 buổi học – 6 giai đoạn",
      timelineSubtitle: "Cấu trúc đào tạo tinh gọn bao trùm toàn bộ chủ đề chuyên môn của HP1",
      timelinePhases: [{
        stageNum: "1",
        phase: "Khởi động",
        sessions: "Buổi 1 – 2",
        tag: "Controller & Khám phá",
        desc: "Làm quen bộ giáo cụ WhalesBot AI Module 1S, tìm hiểu chức năng và nguyên lý hoạt động của Controller."
      }, {
        stageNum: "2",
        phase: "Xây dựng",
        sessions: "Buổi 3 – 4",
        tag: "Gears, Levers & Crank",
        desc: "Chế tạo các cấu trúc truyền động vật lý: hệ thống bánh răng (Gears), đòn bẩy (Levers) và cơ cấu tay quay (Crank)."
      }, {
        stageNum: "3",
        phase: "Lập trình",
        sessions: "Buổi 5 – 6",
        tag: "Block Studio & Sequential programming",
        desc: "Làm quen môi trường Block Studio, lập trình tuần tự (Sequential programming) và điều khiển thông số động cơ (Motor parameters)."
      }, {
        stageNum: "4",
        phase: "Thử nghiệm",
        sessions: "Buổi 7 – 8",
        tag: "Wait, Loops & Angle control",
        desc: "Áp dụng lệnh chờ (Wait), vòng lặp (Loops) và kiểm soát góc quay chính xác (Angle control) để robot vận hành linh hoạt."
      }, {
        stageNum: "5",
        phase: "Hoàn thiện",
        sessions: "Buổi 9 – 10",
        tag: "Tích hợp cơ khí & Thuật toán",
        desc: "Phối hợp Controller, động cơ cùng các cơ cấu Gears, Levers, Crank và tinh chỉnh tối ưu hóa mã lệnh."
      }, {
        stageNum: "6",
        phase: "Dự án cuối khóa",
        sessions: "Buổi 11 – 12",
        tag: "Capstone Project",
        desc: "Hoàn thiện công trình 'Công viên cơ khí', lập trình chu trình tự động hóa hoàn chỉnh và báo cáo kết quả."
      }],
      // Focus Areas
      focusHeading: "Trọng tâm kiến thức & Kỹ năng",
      hardwareTitle: "Cơ khí & Phần cứng",
      hardwareItems: ["Controller", "Motor (Động cơ)", "Gears (Bánh răng)", "Levers (Đòn bẩy)", "Crank (Cơ cấu tay quay)", "Angle control (Kiểm soát góc xoay)"],
      softwareTitle: "Lập trình & Logic thuật toán",
      softwareItems: ["Phần mềm Block Studio", "Lập trình tuần tự (Sequential programming)", "Motor parameters (Thông số động cơ)", "Lệnh chờ (Wait)", "Cấu trúc lặp (Loops)"],
      // Equipment Card
      equipmentTitle: "Thiết bị học tập chuyên dụng",
      equipmentBadge: "Bộ kit chính khóa",
      equipmentName: "WhalesBot AI Module 1S",
      equipmentSpecs: [{
        label: "Nền tảng",
        value: "WhalesBot AI Module 1S"
      }, {
        label: "Yêu cầu máy tính",
        value: "Bắt buộc có máy tính để lập trình và vận hành robot"
      }, {
        label: "Chi phí thiết bị tham khảo",
        value: "~6.500.000 VNĐ / bộ kit"
      }],
      equipmentNote: "* Lưu ý: Đây là chi phí thiết bị tham khảo, không phải học phí. Trung tâm hỗ trợ học viên dùng thiết bị tại phòng Lab hoặc đăng ký mua riêng.",
      // Small Group Model
      smallGroupTitle: "HỌC ROBOTICS THEO NHÓM NHỎ",
      smallGroupSubtitle: "Mô hình tổ chức lớp học linh hoạt, cân bằng giữa trải nghiệm cá nhân và làm việc nhóm",
      smallGroupModels: [{
        ratio: "2 học sinh / bộ kit",
        type: "Mô hình lý tưởng",
        badge: "Tối ưu tương tác",
        desc: "Phân chia rõ ràng giữa vai trò lắp ráp cơ cấu và lập trình thuật toán, giúp cả hai học viên tối đa hóa thời lượng thực hành trực tiếp."
      }, {
        ratio: "3 học sinh / bộ kit",
        type: "Mô hình tối ưu chi phí",
        badge: "Tối ưu đầu tư",
        desc: "Tăng cường năng lực phối hợp làm việc nhóm, chia sẻ giải pháp kỹ thuật đa chiều và giúp tối ưu hóa chi phí đầu tư thiết bị."
      }],
      // Final Project
      finalProjectTag: "Dự án tốt nghiệp khóa học",
      finalProjectTitle: "Dự án cuối khóa",
      finalProjectName: "Công viên cơ khí (Mechanical Park)",
      finalProjectSubtitle: "Dự án capstone chính thức của khóa học HP1 – Kỹ sư cơ khí nhí",
      // Roadmap
      roadmapTitle: "Vị trí trong lộ trình Robotics",
      roadmapSubtitle: "Lộ trình 10 cấp độ đào tạo liên tục từ mầm non đến thi đấu quốc tế",
      currentBadge: "Khóa học hiện tại",
      // What's Next
      whatsNextTag: "Bước tiếp theo",
      whatsNextTitle: "Khóa học kế tiếp: HP2 – Robot biết cảm nhận",
      whatsNextDesc: "Sau khi nắm chắc kết cấu cơ học và lập trình tuần tự với HP1, học viên sẽ tiến lên HP2 để tiếp cận hệ thống cảm biến quang học, chạm và phản hồi thông minh.",
      whatsNextBtn: "XEM HP2 →",
      // Navigation
      prevCourse: "← D4 — Động cơ nhí",
      nextCourse: "HP2 — Robot biết cảm nhận →",
      showcaseTag: "Thực hành dự án",
      showcaseTitle: "Mô hình cơ khí lắp ráp với WhalesBot AI Module 1S"
    },
    en: {
      breadcrumbs: {
        home: "Home",
        pathway: "Robotics Pathway",
        track: "Robotics Ages 7+",
        current: "HP1 – Junior Mechanical Engineer"
      },
      code: "HP1",
      name: "Junior Mechanical Engineer",
      subtitle: "Foundational robotics building & block programming course",
      conceptQuote: "The beginning of genuine robot programming.",
      primaryCta: "BOOK CONSULTATION",
      secondaryCta: "VIEW FULL PATHWAY",
      ageLabel: "Age Group",
      ageValue: "Ages 7+",
      platformLabel: "Platform",
      platformValue: "WhalesBot AI Module 1S",
      durationLabel: "Duration",
      durationValue: "12 sessions × 90 mins",
      groupSizeLabel: "Group Size",
      groupSizeValue: "2–3 students/kit",
      prerequisiteLabel: "Prerequisite",
      prerequisiteValue: "Completed D4 or per entrance requirements",
      // Target Audience
      targetAudienceTitle: "Who is this course for?",
      targetAudienceBody: "For learners aged 7+, after D4 or per the program's entrance requirements. HP1 is the transition to real robot programming with WhalesBot AI Module 1S.",
      // 4-Step Methodology
      methodologyTitle: "4-Step Learning Methodology",
      methodologySubtitle: "A structured lab process fostering engineering logic and algorithmic programming",
      methodologySteps: [{
        num: "01",
        title: "Warm-up",
        desc: "Explore authentic mechanical questions, analyze physical mechanisms, and outline session objectives."
      }, {
        num: "02",
        title: "Build / Practice",
        desc: "Construct mechanisms incorporating the controller, motors, gear trains, levers, and crank linkages."
      }, {
        num: "03",
        title: "Program / Think",
        desc: "Utilize Block Studio to build sequential routines, regulate motor parameters, and configure control loops."
      }, {
        num: "04",
        title: "Present",
        desc: "Benchmark mechanism performance, verify rotation angles, and present technical outcomes to peers."
      }],
      // 12 Sessions in 6 Stages
      timelineTitle: "12-Session Curriculum – 6 Stages",
      timelineSubtitle: "Curriculum progression built strictly around HP1's official technical topics",
      timelinePhases: [{
        stageNum: "1",
        phase: "Warm-up",
        sessions: "Sessions 1 – 2",
        tag: "Controller & Discovery",
        desc: "Familiarization with WhalesBot AI Module 1S, understanding Controller architecture and core connections."
      }, {
        stageNum: "2",
        phase: "Build",
        sessions: "Sessions 3 – 4",
        tag: "Gears, Levers & Crank",
        desc: "Assembling fundamental mechanical transmissions: gear trains (Gears), lever systems (Levers), and crank mechanisms (Crank)."
      }, {
        stageNum: "3",
        phase: "Programming",
        sessions: "Sessions 5 – 6",
        tag: "Block Studio & Sequential programming",
        desc: "Hands-on workflow with Block Studio, establishing sequential programming routines and tuning motor parameters."
      }, {
        stageNum: "4",
        phase: "Testing",
        sessions: "Sessions 7 – 8",
        tag: "Wait, Loops & Angle control",
        desc: "Implementing time delays (Wait), execution loops (Loops), and precise rotational limits (Angle control)."
      }, {
        stageNum: "5",
        phase: "Refine",
        sessions: "Sessions 9 – 10",
        tag: "Hardware & Logic Integration",
        desc: "Harmonizing Controller and motors with multi-stage Gears, Levers, and Crank assemblies while debugging code."
      }, {
        stageNum: "6",
        phase: "Final Project",
        sessions: "Sessions 11 – 12",
        tag: "Capstone Project",
        desc: "Finalizing the 'Mechanical Park' capstone build, executing automated operation, and defending engineering designs."
      }],
      // Focus Areas
      focusHeading: "Core Focus & Learning Topics",
      hardwareTitle: "Mechanical & Hardware",
      hardwareItems: ["Controller", "Motor", "Gears", "Levers", "Crank", "Angle control"],
      softwareTitle: "Coding & Logic Algorithms",
      softwareItems: ["Block Studio", "Sequential programming", "Motor parameters", "Wait", "Loops"],
      // Equipment Card
      equipmentTitle: "Dedicated Learning Hardware",
      equipmentBadge: "Official Kit",
      equipmentName: "WhalesBot AI Module 1S",
      equipmentSpecs: [{
        label: "Platform",
        value: "WhalesBot AI Module 1S"
      }, {
        label: "Computer Requirement",
        value: "Computer is required for programming and robot operation"
      }, {
        label: "Reference Equipment Cost",
        value: "~6,500,000 VNĐ / kit"
      }],
      equipmentNote: "* Note: This is reference equipment cost, not tuition. Students may use equipment provided in the lab or purchase separately.",
      // Small Group Model
      smallGroupTitle: "SMALL-GROUP ROBOTICS LEARNING",
      smallGroupSubtitle: "Flexible learning configurations balancing personal hands-on practice with collaborative synergy",
      smallGroupModels: [{
        ratio: "2 students / kit",
        type: "Ideal model",
        badge: "High Engagement",
        desc: "Distinct division between mechanical building and code programming, maximizing individual hands-on trial time."
      }, {
        ratio: "3 students / kit",
        type: "Cost-optimized model",
        badge: "Collaborative Value",
        desc: "Enhances team problem-solving dynamics, multifaceted technical discussion, and optimizes overall equipment expenditure."
      }],
      // Final Project
      finalProjectTag: "Course Graduation Capstone",
      finalProjectTitle: "Final Project",
      finalProjectName: "Mechanical Park (Công viên cơ khí)",
      finalProjectSubtitle: "Official capstone project for course HP1 – Junior Mechanical Engineer",
      // Roadmap
      roadmapTitle: "Position in Robotics Pathway",
      roadmapSubtitle: "Tracking 10 milestone courses from early childhood through international competition",
      currentBadge: "Current Course",
      // What's Next
      whatsNextTag: "Next Step",
      whatsNextTitle: "Next Course: HP2 – Sensing Robots",
      whatsNextDesc: "After solidifying mechanical mechanisms and sequential coding in HP1, students advance to HP2 to master optical, tactile, and ultrasonic feedback sensors.",
      whatsNextBtn: "VIEW HP2 →",
      // Navigation
      prevCourse: "← D4 — Junior Motors",
      nextCourse: "HP2 — Sensing Robots →",
      showcaseTag: "Project Practice",
      showcaseTitle: "Mechanical Project Assembly with WhalesBot AI Module 1S"
    }
  };
  const t = content[lang];
  const pathwayNodes = [{
    code: "D1",
    nameVi: "Mầm non 1",
    nameEn: "Early 1",
    link: "/RoboticsCourseD1Page",
    imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d1.png",
    imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d1.png-wvc-srcset",
    imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d1.png-wvc-sizes"
  }, {
    code: "D2",
    nameVi: "Mầm non 2",
    nameEn: "Early 2",
    link: "/RoboticsCourseD2Page",
    imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d2.png",
    imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d2.png-wvc-srcset",
    imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d2.png-wvc-sizes"
  }, {
    code: "D3",
    nameVi: "Cơ khí nhí",
    nameEn: "Junior Mech",
    link: "/RoboticsCourseD3Page",
    imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d3.png",
    imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d3.png-wvc-srcset",
    imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d3.png-wvc-sizes"
  }, {
    code: "D4",
    nameVi: "Động cơ nhí",
    nameEn: "Junior Motors",
    link: "/RoboticsCourseD4Page",
    imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d4.png",
    imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d4.png-wvc-srcset",
    imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d4.png-wvc-sizes"
  }, {
    code: "HP1",
    nameVi: "Kỹ sư cơ khí",
    nameEn: "Junior Mech Eng",
    link: "/RoboticsCourseHP1Page",
    isCurrent: true,
    imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp1.webp",
    imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp1.webp-wvc-srcset",
    imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp1.webp-wvc-sizes"
  }, {
    code: "HP2",
    nameVi: "Robot cảm nhận",
    nameEn: "Sensing Robots",
    link: "/RoboticsCourseHP2Page",
    imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp2.png",
    imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp2.png-wvc-srcset",
    imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp2.png-wvc-sizes"
  }, {
    code: "HP3",
    nameVi: "Robot thông minh",
    nameEn: "Smart Robots",
    link: "/RoboticsCourseHP3Page",
    imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp3.png",
    imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp3.png-wvc-srcset",
    imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp3.png-wvc-sizes"
  }, {
    code: "HP4",
    nameVi: "AI Sơ cấp",
    nameEn: "AI Primer",
    link: "/RoboticsCourseHP4Page",
    imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp4.png",
    imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp4.png-wvc-srcset",
    imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp4.png-wvc-sizes"
  }, {
    code: "HP5",
    nameVi: "AI Chuyên sâu",
    nameEn: "AI Advanced",
    link: "/RoboticsCourseHp5Page",
    imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp5.png",
    imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp5.png-wvc-srcset",
    imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp5.png-wvc-sizes"
  }, {
    code: "HP6",
    nameVi: "Thi đấu",
    nameEn: "Competition",
    link: "/RoboticsCourseHP6Page",
    imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp6.png",
    imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp6.png-wvc-srcset",
    imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp6.png-wvc-sizes"
  }];
  return <section id="coursedetailhp1" data-nav="light" className="bg-background text-foreground py-10 md:py-16 lg:py-20 border-b border-border transition-colors">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {/* (1) 4-level Breadcrumb with Breadcrumb components & Language Switcher */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-6 mb-8 border-b border-border">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink to="/">
                  {t.breadcrumbs.home}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink to="/RoboticsLearningPathwayPage">
                  {t.breadcrumbs.pathway}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink to="/robotics-learning-pathway#whalesbotprogrammingtrack">
                  {t.breadcrumbs.track}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>
                  {t.breadcrumbs.current}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="inline-flex items-center gap-1.5 p-1 bg-muted rounded-lg border border-border shrink-0 self-end sm:self-auto">
            <Toggle size="sm" pressed={lang === "vi"} onPressedChange={pressed => pressed && setLang("vi")} className="text-xs font-semibold px-2.5 py-1 rounded-md data-[state=on]:bg-card data-[state=on]:text-foreground data-[state=on]:shadow-2xs">
              <Languages className="size-3.5 mr-1" />
              VI
            </Toggle>
            <Toggle size="sm" pressed={lang === "en"} onPressedChange={pressed => pressed && setLang("en")} className="text-xs font-semibold px-2.5 py-1 rounded-md data-[state=on]:bg-card data-[state=on]:text-foreground data-[state=on]:shadow-2xs">
              <Languages className="size-3.5 mr-1" />
              EN
            </Toggle>
          </div>
        </div>

        {/* Hero Section & Dual CTAs + Hero Visual */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start mb-12">
          <div className="lg:col-span-8 space-y-5">
            <div className="flex flex-wrap items-center gap-3">
              <Badge variant="default" className="text-sm font-bold tracking-wider px-3 py-1">
                {t.code}
              </Badge>
              <Badge variant="secondary" className="text-xs font-medium">
                {t.ageValue}
              </Badge>
              <span className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">
                WHALESBOT AI MODULE 1S
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-serif">
              {t.name}
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              {t.subtitle}
            </p>

            {/* (2) Dual CTAs in Hero */}
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold shadow-xs">
                <Link to="/FreeTrialContactPage">
                  <Sparkles className="size-4 mr-2" />
                  <span>{t.primaryCta}</span>
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground font-semibold">
                <Link to="/RoboticsLearningPathwayPage">
                  <Compass className="size-4 mr-2" />
                  <span>{t.secondaryCta}</span>
                </Link>
              </Button>
            </div>
          </div>

          {/* Hero Visual Card & Concept Quote Callout */}
          <div className="lg:col-span-4 w-full space-y-4">
            <div className="relative rounded-xl overflow-hidden border border-border bg-card shadow-sm group">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_ai_module_1s_kit.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_ai_module_1s_kit.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_ai_module_1s_kit.png-wvc-sizes" alt="WhalesBot AI Module 1S kit and gear lever crank mechanical project" className="w-full h-52 sm:h-60 object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent pointer-events-none" />
              <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                <Badge variant="default" className="text-[10px] font-bold uppercase tracking-wider bg-primary text-primary-foreground">
                  WhalesBot 1S Kit
                </Badge>
                <span className="text-[11px] font-semibold text-foreground/90 bg-background/80 backdrop-blur-xs px-2.5 py-0.5 rounded-md border border-border">
                  Gears & Levers
                </span>
              </div>
            </div>

            <Card className="border-border bg-card shadow-xs relative overflow-hidden rounded-xl">
              <div className="absolute top-0 left-0 w-1.5 h-full bg-accent" />
              <CardContent className="pt-6 pb-6 pl-6 pr-6">
                <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold block mb-2">
                  Concept
                </span>
                <blockquote className="text-base sm:text-lg font-semibold italic text-foreground leading-snug">
                  "{t.conceptQuote}"
                </blockquote>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Metadata Badges / Info Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-14">
          <Card className="border-border bg-card shadow-xs rounded-xl">
            <CardContent className="p-5 flex flex-col gap-2">
              <div className="flex items-center gap-2 text-muted-foreground">
                <GraduationCap className="size-4 text-primary" />
                <span className="text-xs font-medium uppercase tracking-wider">
                  {t.ageLabel}
                </span>
              </div>
              <p className="text-base font-semibold text-foreground">
                {t.ageValue}
              </p>
            </CardContent>
          </Card>

          <Card className="border-border bg-card shadow-xs rounded-xl">
            <CardContent className="p-5 flex flex-col gap-2">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Cpu className="size-4 text-primary" />
                <span className="text-xs font-medium uppercase tracking-wider">
                  {t.platformLabel}
                </span>
              </div>
              <p className="text-sm sm:text-base font-semibold text-foreground">
                {t.platformValue}
              </p>
            </CardContent>
          </Card>

          <Card className="border-border bg-card shadow-xs rounded-xl">
            <CardContent className="p-5 flex flex-col gap-2">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="size-4 text-primary" />
                <span className="text-xs font-medium uppercase tracking-wider">
                  {t.durationLabel}
                </span>
              </div>
              <p className="text-base font-semibold text-foreground">
                {t.durationValue}
              </p>
            </CardContent>
          </Card>

          <Card className="border-border bg-card shadow-xs rounded-xl">
            <CardContent className="p-5 flex flex-col gap-2">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Users className="size-4 text-primary" />
                <span className="text-xs font-medium uppercase tracking-wider">
                  {t.groupSizeLabel}
                </span>
              </div>
              <p className="text-base font-semibold text-foreground">
                {t.groupSizeValue}
              </p>
            </CardContent>
          </Card>

          <Card className="border-border bg-card shadow-xs rounded-xl sm:col-span-2 lg:col-span-1">
            <CardContent className="p-5 flex flex-col gap-2">
              <div className="flex items-center gap-2 text-muted-foreground">
                <CheckCircle2 className="size-4 text-primary" />
                <span className="text-xs font-medium uppercase tracking-wider">
                  {t.prerequisiteLabel}
                </span>
              </div>
              <p className="text-xs sm:text-sm font-semibold text-foreground leading-snug">
                {t.prerequisiteValue}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* (3) 'Who is this course for?' section */}
        <div className="mb-14 p-6 sm:p-8 rounded-xl bg-card border border-border shadow-xs">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-secondary/15 text-secondary-foreground text-xs font-bold uppercase tracking-wider mb-2">
              <span>Audience Target</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground font-serif mb-4">
              {t.targetAudienceTitle}
            </h2>
            <div className="p-5 rounded-lg bg-muted/60 border border-border">
              <p className="text-base sm:text-lg text-foreground font-medium leading-relaxed">
                {t.targetAudienceBody}
              </p>
            </div>
          </div>
        </div>

        {/* (5) 4-step Learning Method Section */}
        <div className="space-y-6 mb-14">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-secondary/15 text-secondary-foreground text-xs font-bold uppercase tracking-wider mb-2">
              <span>Learning Cycle</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground font-serif">
              {t.methodologyTitle}
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground mt-1">
              {t.methodologySubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {t.methodologySteps.map((step, idx) => {
            const icons = [<Lightbulb className="size-5 text-accent" />, <Wrench className="size-5 text-secondary" />, <Monitor className="size-5 text-primary" />, <Presentation className="size-5 text-primary" />];
            return <Card key={idx} data-index={idx} className="border-border bg-card shadow-xs rounded-xl relative overflow-hidden group">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-border group-hover:bg-primary transition-colors" />
                  <CardContent className="p-6 flex flex-col gap-3">
                    <div className="flex items-center justify-between">
                      <div className="p-2.5 rounded-lg bg-muted border border-border">
                        {icons[idx]}
                      </div>
                      <span className="text-xl font-bold font-serif text-muted-foreground/60">
                        {step.num}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-foreground">
                      {step.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {step.desc}
                    </p>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

        {/* (4) 12-session timeline grouped into 6 stages */}
        <div className="space-y-6 mb-14">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-secondary/15 text-secondary-foreground text-xs font-bold uppercase tracking-wider mb-2">
              <span>Curriculum Structure</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground font-serif">
              {t.timelineTitle}
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground mt-1">
              {t.timelineSubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {t.timelinePhases.map((phase, idx) => <div key={idx} data-index={idx} className="p-5 sm:p-6 rounded-xl bg-card border border-border shadow-xs flex flex-col justify-between gap-4 hover:border-secondary transition-colors">
                <div>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <span className="px-2.5 py-1 rounded-md bg-primary/10 text-primary font-bold text-xs uppercase tracking-wider">
                      {phase.sessions}
                    </span>
                    <Badge variant="outline" className="text-[11px] font-medium border-border text-muted-foreground">
                      Stage {phase.stageNum}
                    </Badge>
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-1">
                    {phase.phase}
                  </h3>
                  <span className="inline-block text-xs font-semibold text-secondary mb-2">
                    {phase.tag}
                  </span>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {phase.desc}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* Mid-Page Visual Showcase: WhalesBot Mechanical Project */}
        <div className="mb-14 rounded-xl border border-border bg-card shadow-xs overflow-hidden grid grid-cols-1 lg:grid-cols-12 items-center">
          <div className="lg:col-span-7 relative h-64 sm:h-80 lg:h-full min-h-[280px] overflow-hidden group">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_mechanical_project_build.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_mechanical_project_build.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_mechanical_project_build.png-wvc-sizes" alt="Mechanical project built with the WhalesBot robotics platform" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-card/90 hidden lg:block" />
          </div>
          <div className="lg:col-span-5 p-6 sm:p-8 space-y-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-secondary/15 text-secondary-foreground text-xs font-bold uppercase tracking-wider">
              <Cog className="size-3.5" />
              <span>{t.showcaseTag}</span>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-foreground font-serif">
              {t.showcaseTitle}
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              WhalesBot AI Module 1S – Platform
            </p>
            <div className="pt-2 flex items-center gap-2 text-xs font-semibold text-primary">
              <Check className="size-4 text-secondary shrink-0" />
              <span>Gears, Levers, Crank & Motor Integration</span>
            </div>
          </div>
        </div>

        {/* Existing Hardware & Software Focus Lists */}
        <div className="space-y-6 mb-14">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground font-serif">
            {t.focusHeading}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Hardware & Mechanical */}
            <Card className="border-border bg-card shadow-xs rounded-xl">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 pb-4 mb-4 border-b border-border">
                  <div className="p-2.5 rounded-lg bg-secondary/15 text-secondary-foreground">
                    <Cog className="size-5" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground">
                    {t.hardwareTitle}
                  </h3>
                </div>

                <ul className="space-y-2.5">
                  {t.hardwareItems.map((item, idx) => <li key={idx} data-index={idx} className="flex items-center gap-2.5 text-sm text-foreground/90 font-medium">
                      <span className="size-1.5 rounded-lg bg-primary shrink-0" />
                      <span>{item}</span>
                    </li>)}
                </ul>
              </CardContent>
            </Card>

            {/* Software & Logic */}
            <Card className="border-border bg-card shadow-xs rounded-xl">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 pb-4 mb-4 border-b border-border">
                  <div className="p-2.5 rounded-lg bg-accent/20 text-accent-foreground">
                    <Code2 className="size-5" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground">
                    {t.softwareTitle}
                  </h3>
                </div>

                <ul className="space-y-2.5">
                  {t.softwareItems.map((item, idx) => <li key={idx} data-index={idx} className="flex items-center gap-2.5 text-sm text-foreground/90 font-medium">
                      <span className="size-1.5 rounded-lg bg-accent shrink-0" />
                      <span>{item}</span>
                    </li>)}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* (6) Equipment Card & (8) Final Project Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-14">
          {/* (6) Equipment Card */}
          <Card className="border-border bg-card shadow-xs rounded-xl overflow-hidden flex flex-col justify-between">
            <CardContent className="p-6 sm:p-8 flex flex-col gap-5">
              <div className="flex items-center gap-3 pb-4 border-b border-border">
                <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                  <Laptop className="size-5" />
                </div>
                <div>
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">
                    {t.equipmentTitle}
                  </span>
                  <h3 className="text-xl font-bold text-foreground font-serif">
                    {t.equipmentName}
                  </h3>
                </div>
              </div>

              <div className="relative rounded-lg overflow-hidden border border-border bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_robotics_kit.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_robotics_kit.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_robotics_kit.png-wvc-sizes" alt="WhalesBot AI Module 1S robotics kit used in ROBOTO's HP1–HP6 engineering track" className="w-full h-44 object-cover" loading="lazy" />
              </div>

              <div className="space-y-3">
                {t.equipmentSpecs.map((spec, idx) => <div key={idx} data-index={idx} className="p-3.5 rounded-lg bg-muted/60 border border-border flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                    <span className="text-xs font-semibold text-muted-foreground">
                      {spec.label}:
                    </span>
                    <span className="text-sm font-bold text-foreground">
                      {spec.value}
                    </span>
                  </div>)}
              </div>

              <div className="p-3 rounded-lg bg-accent/10 border border-accent/20">
                <p className="text-xs text-muted-foreground leading-relaxed italic">
                  {t.equipmentNote}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* (8) Final Project Section: Mechanical Park */}
          <Card className="border-border bg-card shadow-xs rounded-xl overflow-hidden flex flex-col justify-between">
            <CardContent className="p-6 sm:p-8 flex flex-col gap-5">
              <div className="flex items-center gap-3 pb-4 border-b border-border">
                <div className="p-2.5 rounded-lg bg-accent/20 text-accent-foreground">
                  <FerrisWheel className="size-5" />
                </div>
                <div>
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">
                    {t.finalProjectTag}
                  </span>
                  <h3 className="text-xl font-bold text-foreground font-serif">
                    {t.finalProjectTitle}
                  </h3>
                </div>
              </div>

              <div className="p-6 rounded-xl bg-muted/40 border border-border flex flex-col gap-2">
                <div className="flex items-center gap-2 text-primary font-bold text-xs uppercase tracking-wider">
                  <Sparkles className="size-4 text-accent" />
                  <span>Capstone Highlight</span>
                </div>
                <h4 className="text-2xl font-bold text-foreground font-serif">
                  {t.finalProjectName}
                </h4>
                <p className="text-sm text-muted-foreground leading-relaxed mt-1">
                  {t.finalProjectSubtitle}
                </p>
              </div>

              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Check className="size-4 text-secondary shrink-0" />
                <span>Hoàn thành chuỗi 12 buổi học với chứng nhận ROBOTO Junior Mechanical Engineer</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* (7) Small-group Learning Model Section */}
        <div className="mb-14 p-6 sm:p-8 rounded-xl bg-card border border-border shadow-xs">
          <div className="mb-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-secondary/15 text-secondary-foreground text-xs font-bold uppercase tracking-wider mb-2">
              <span>Class Organization</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground font-serif">
              {t.smallGroupTitle}
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground mt-1">
              {t.smallGroupSubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {t.smallGroupModels.map((model, idx) => <div key={idx} data-index={idx} className="p-6 rounded-xl bg-muted/50 border border-border flex flex-col justify-between gap-4">
                <div>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      {model.type}
                    </span>
                    <Badge variant="outline" className="text-xs font-semibold border-border">
                      {model.badge}
                    </Badge>
                  </div>
                  <h3 className="text-xl font-bold text-foreground font-serif mb-2">
                    {model.ratio}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {model.desc}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* (9) 10-node Pathway Position Map */}
        <div className="mb-14 p-6 sm:p-8 rounded-xl bg-card border border-border shadow-xs">
          <div className="mb-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-secondary/15 text-secondary-foreground text-xs font-bold uppercase tracking-wider mb-2">
              <Layers className="size-3.5" />
              <span>Full Roadmap Alignment</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground font-serif">
              {t.roadmapTitle}
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground mt-1">
              {t.roadmapSubtitle}
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 lg:grid-cols-10 gap-2">
            {pathwayNodes.map((node, idx) => {
            const nodeName = lang === "vi" ? node.nameVi : node.nameEn;
            const nodeAlt = `${node.code} robotics course thumbnail`;
            if (node.isCurrent) {
              return <div key={idx} data-index={idx} className="p-2.5 rounded-lg border-2 border-primary bg-primary/10 text-center flex flex-col items-center justify-center relative shadow-xs">
                    <img src={node.imgSrc} data-wvc-srcset={node.imgSrcset} data-wvc-sizes={node.imgSizes} alt={nodeAlt} className="w-10 h-10 object-cover rounded-md mb-1.5" loading="lazy" />
                    <span className="text-[10px] font-bold text-primary uppercase tracking-wider mb-0.5">
                      {node.code}
                    </span>
                    <span className="text-xs font-bold text-foreground leading-tight">
                      {nodeName}
                    </span>
                    <span className="mt-1 text-[9px] px-1.5 py-0.5 rounded-lg bg-primary text-primary-foreground font-semibold">
                      Current
                    </span>
                  </div>;
            }
            return <Link key={idx} data-index={idx} to={node.link} className="p-2.5 rounded-lg border border-border bg-muted/40 hover:bg-muted hover:border-primary/50 text-center flex flex-col items-center justify-center transition-colors group">
                  <img src={node.imgSrc} data-wvc-srcset={node.imgSrcset} data-wvc-sizes={node.imgSizes} alt={nodeAlt} className="w-10 h-10 object-cover rounded-md mb-1.5 group-hover:scale-105 transition-transform" loading="lazy" />
                  <span className="text-[10px] font-bold text-muted-foreground group-hover:text-primary uppercase tracking-wider mb-0.5">
                    {node.code}
                  </span>
                  <span className="text-xs font-medium text-foreground/90 group-hover:text-foreground leading-tight">
                    {nodeName}
                  </span>
                </Link>;
          })}
          </div>
        </div>

        {/* (10) 'What's next' Prominent Card pointing to HP2 */}
        <div className="mb-10 p-6 sm:p-8 rounded-xl bg-card border-2 border-primary/30 shadow-xs flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-2 max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-wider text-primary">
              {t.whatsNextTag}
            </span>
            <h3 className="text-xl sm:text-2xl font-bold text-foreground font-serif">
              {t.whatsNextTitle}
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t.whatsNextDesc}
            </p>
          </div>

          <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shrink-0 shadow-xs">
            <Link to="/RoboticsCourseHP2Page">
              <span>{t.whatsNextBtn}</span>
            </Link>
          </Button>
        </div>

        {/* Previous & Next Course Navigation */}
        <div className="pt-6 border-t border-border flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
          <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-semibold border-border justify-start sm:justify-center">
            <Link to="/RoboticsCourseD4Page">
              <ArrowLeft className="size-4 mr-2" />
              <span>{t.prevCourse}</span>
            </Link>
          </Button>

          <Button asChild variant="default" size="lg" className="w-full sm:w-auto font-semibold justify-end sm:justify-center">
            <Link to="/RoboticsCourseHP2Page">
              <span>{t.nextCourse}</span>
              <ArrowRight className="size-4 ml-2" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}